import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Building2,
  Lock,
  Video,
  Image,
  Mail,
  MessageSquare,
  Bot,
  User,
  Compass,
  FileText,
  Megaphone,
  Archive,
  BookOpen,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import theme from '../theme';

const SidebarContainer = styled(motion.aside)`
  width: ${props => props.$collapsed ? '72px' : '280px'};
  background: ${theme.colors.sidebar};
  backdrop-filter: blur(12px);
  border-right: 1px solid ${theme.colors.borderLight};
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 100;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 16px;
  color: ${theme.colors.text.primary};
  overflow: hidden;
`;

// const LogoIcon = styled.img`
//   width: 32px;
//   height: 32px;
//   border-radius: 8px;
//   flex-shrink: 0;
// `;

const LogoText = styled.span`
  white-space: nowrap;
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.2s ease;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${theme.colors.text.muted};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.navHover};
    color: ${theme.colors.text.primary};
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.scrollbarThumb};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.scrollbarThumbHover};
  }
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.text.muted};
  padding: 12px 12px 8px;
  white-space: nowrap;
  overflow: hidden;
`;

const NavItem = styled(motion.button)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  color: ${theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background: ${theme.colors.navHover};
    color: ${theme.colors.text.primary};
  }

  &.active {
    background: ${theme.colors.navActive};
    color: ${theme.colors.text.primary};
  }

  &.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 20px;
    background: ${theme.colors.primary};
    border-radius: 0 2px 2px 0;
  }
`;

const NavItemIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavItemText = styled.span`
  margin-left: 16px;

  flex: 0;

  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.2s ease;

  white-space: nowrap;
`;

const Badge = styled.span`
  margin-left: auto;

  background: #EF4444;
  color: white;

  font-size: 11px;
  font-weight: 600;

  padding: 2px 8px;
  border-radius: 9999px;
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  color: ${theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background: ${theme.colors.navHover};
    color: ${theme.colors.text.primary};
  }
`;

const SectionContent = styled(motion.div)`
  padding-left: 24px;
  overflow: hidden;
`;

const SidebarFooter = styled.div`
  padding: 12px;
  border-top: 1px solid ${theme.colors.borderLight};
`;

const MobileOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileToggle = styled.button`
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 101;
  background: ${theme.colors.sidebar};
  border: 1px solid ${theme.colors.borderLight};
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: flex;
  }
`;

const navigation = [
  {
    section: 'Dashboard',
    items: [
      { id: 'overview', label: 'Dashboard', icon: LayoutDashboard }
    ]
  },
  {
    section: 'Management',
    items: [
      { id: 'students', label: 'Student Verification', icon: Users, badge: 'unverifiedStudentCount' },
      { id: 'colleges', label: 'Colleges', icon: GraduationCap },
      { id: 'universities', label: 'Universities', icon: Building2 },
      { id: 'ip-management', label: 'IP Management', icon: Lock },
      { id: 'live-sessions', label: 'Live Sessions', icon: Video },
      { id: 'industry-images', label: 'Industry Images', icon: Image }
    ]
  },
  {
    section: 'Communication',
    items: [
      { id: 'inquiries', label: 'Inquiries', icon: Mail, badge: 'unresolvedCount' },
      { id: 'feedback', label: 'Feedback', icon: MessageSquare, badge: 'pendingFeedbackCount' },
      { id: 'chatbot', label: 'Chatbot', icon: Bot },
      { id: 'experts', label: 'Experts', icon: User }
    ]
  },
  {
    section: 'Content',
    items: [
      { id: 'blogs', label: 'Blogs', icon: FileText },
      { id: 'latest-updates', label: 'Latest Updates', icon: Megaphone },
      { id: 'jars', label: 'Jars', icon: Archive },
      { id: 'subheadings', label: 'Subheadings', icon: FileText },
      { id: 'learning-resources', label: 'Learning Resources', icon: BookOpen }
    ]
  }
];

const Sidebar = ({ activeTab, onNavigate, onLogout, counts, isMobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [advisoryExpanded, setAdvisoryExpanded] = useState(false);
  // const [isMobile, setIsMobile] = useState(false);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const getBadgeCount = (badgeKey) => {
    if (!badgeKey || !counts) return 0;
    return counts[badgeKey] || 0;
  };

  return (
    <>
      <MobileToggle onClick={onMobileClose}>
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </MobileToggle>

      <MobileOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onMobileClose}
        isOpen={isMobileOpen}
      />

      <SidebarContainer
        $collapsed={collapsed}
        initial={false}
        animate={{ width: collapsed ? 72 : 280 }}
        transition={{ duration: 0.3 }}
      >
        <SidebarHeader>
          <Logo>
            {/* <LogoIcon src="/white_logo.svg" alt="Logo" /> */}
            <LogoText $collapsed={collapsed}>Admin Portal</LogoText>
          </Logo>
          <CollapseButton onClick={handleCollapse}>
            {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          </CollapseButton>
        </SidebarHeader>

        <SidebarContent>
          {navigation.map((section) => (
            <div key={section.section}>
              <SectionLabel $collapsed={collapsed}>{section.section}</SectionLabel>
              {section.items.map((item) => (
                <NavItem
                  key={item.id}
                  className={activeTab === item.id ? 'active' : ''}
                  onClick={() => {
                    onNavigate(item.id);
                    if (isMobileOpen) onMobileClose();
                  }}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <NavItemIcon>
                    <item.icon size={20} />
                  </NavItemIcon>
                  <NavItemText $collapsed={collapsed}>{item.label}</NavItemText>
                  {item.badge && getBadgeCount(item.badge) > 0 && (
                    <Badge>{getBadgeCount(item.badge)}</Badge>
                  )}
                </NavItem>
              ))}
            </div>
          ))}

          <SectionLabel $collapsed={collapsed}>Advisory</SectionLabel>
          <SectionHeader onClick={() => setAdvisoryExpanded(!advisoryExpanded)}>
            <NavItemIcon>
              <Compass size={20} />
            </NavItemIcon>
            <NavItemText $collapsed={collapsed}>Advisory Management</NavItemText>
            {!collapsed && (advisoryExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </SectionHeader>
          <AnimatePresence>
            {advisoryExpanded && !collapsed && (
              <SectionContent
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <NavItem
                  className={activeTab === 'advisory-members' ? 'active' : ''}
                  onClick={() => {
                    onNavigate('advisory-members');
                    if (isMobileOpen) onMobileClose();
                  }}
                  whileHover={{ x: 4 }}
                >
                  <NavItemIcon>
                    <Users size={18} />
                  </NavItemIcon>
                  <NavItemText>Advisory Members</NavItemText>
                </NavItem>
                <NavItem
                  className={activeTab === 'advisory-categories' ? 'active' : ''}
                  onClick={() => {
                    onNavigate('advisory-categories');
                    if (isMobileOpen) onMobileClose();
                  }}
                  whileHover={{ x: 4 }}
                >
                  <NavItemIcon>
                    <Archive size={18} />
                  </NavItemIcon>
                  <NavItemText>Advisory Categories</NavItemText>
                </NavItem>
              </SectionContent>
            )}
          </AnimatePresence>
        </SidebarContent>

        <SidebarFooter>
          <NavItem
            onClick={onLogout}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
          >
            <NavItemIcon>
              <LogOut size={20} />
            </NavItemIcon>
            <NavItemText $collapsed={collapsed}>Logout</NavItemText>
          </NavItem>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
